
/* ============================== src/components/ModalRenderer.jsx ============================== */
// src/components/ModalRenderer.jsx
/**
 * Центральный рендер модалок по modal.type. Провайдит db-контекст
 * (tasks/projects/employees/… — через React Query), обрабатывает save/
 * delete-колбэки от модалок и логику «возврата» на исходный экран
 * (returnToTask/returnToProject/returnToEmployeeTasks/returnToTag).
 *
 * handleTaskSave(result, isNew, meta) — после успешного сохранения
 * TaskModal. Если isNew && meta.templateSubtasks.length > 0 —
 * материализует подзадачи через POST /templates/apply
 * (useInstantiateTemplateMutation): бэк создаёт дерево рекурсивно,
 * возвращает { created, failed, errors }. Модалка закрывается сразу,
 * материализация идёт в фоне: пользователь не ждёт 5–15 с при шаблоне
 * с десятками подзадач; тост появляется по завершении (успех / warning
 * с числами / warning с message при сетевой ошибке). Ошибки не теряются
 * (catch гарантирует отсутствие unhandled rejection). Закрытие модалки
 * не отменяет мутацию — она живёт в QueryClient.
 *
 * handleProjectSave — без материализации: ProjectModal.saveHandler сам
 * вызывает useInstantiateTemplateMutation для pendingTemplateTasks и
 * затем onSave(result, isNew). ModalRenderer здесь только закрывает.
 *
 * unwrapItems и MAX_REF_LIMIT — в utils/dbShape.js (общая точка для
 * справочных выборок и нормализации конверта { items, meta }).
 */
import { useCallback, useMemo } from "react";
import {
  TaskModal,
  ProjectModal,
  ChangeRequestModal,
  RolesModal,
  DeptsModal,
  VacationModal,
  DelegationModal,
  VacNowModal,
  CreateEmployeeModal,
  EditEmployeeModal,
  YearCalendarModal,
  EmployeeTasksModal,
  TagResultsModal,
} from "./Modals";
import { TemplateModal } from "./Templates";
import { collectTaskPayloads } from "../utils/templateNesting";
import { HelpModal } from "./Modals/HelpModal";
import { useEmployeesQuery } from "../api/queries/employees";
import { useDepartmentsQuery } from "../api/queries/departments";
import { useKbsQuery } from "../api/queries/kbs";
import { useTasksQuery } from "../api/queries/tasks";
import { useProjectsQuery } from "../api/queries/projects";
import { useVacationsQuery } from "../api/queries/vacations";
import { useRoleDelegationsQuery } from "../api/queries/role-delegations";
import { useCreateChangeRequestMutation } from "../api/queries/change-requests";
import { useInstantiateTemplateMutation } from "../api/queries/templates";
import {
  useUpsertVacationMutation,
  useDeleteVacationMutation,
} from "../api/queries/vacations";
import { useCreateRoleDelegationMutation } from "../api/queries/role-delegations";
import { unwrapItems, MAX_REF_LIMIT } from "../utils/dbShape";

export default function ModalRenderer({
  modal,
  onClose,
  ur,
  openTask,
  openProject,
  openChangeReq,
  openEmployeeTasks,
  openCopyTask,
  openCopyProject,
  openTag,
  onTabChange,
  toast,
}) {
  const { data: tasksRes } = useTasksQuery({ limit: MAX_REF_LIMIT });
  const { data: projectsRes } = useProjectsQuery({ limit: MAX_REF_LIMIT });
  const { data: vacationsRes } = useVacationsQuery();
  const { data: roleDelegationsRes } = useRoleDelegationsQuery();

  const { data: employeesRes } = useEmployeesQuery();
  const { data: departmentsRes } = useDepartmentsQuery();
  const { data: kbsRes } = useKbsQuery();

  const tasks = useMemo(() => unwrapItems(tasksRes), [tasksRes]);
  const projects = useMemo(() => unwrapItems(projectsRes), [projectsRes]);
  const vacations = useMemo(() => unwrapItems(vacationsRes), [vacationsRes]);
  const roleDelegations = useMemo(
    () => unwrapItems(roleDelegationsRes),
    [roleDelegationsRes],
  );
  const employees = useMemo(() => unwrapItems(employeesRes), [employeesRes]);
  const departments = useMemo(
    () => unwrapItems(departmentsRes),
    [departmentsRes],
  );
  const kbs = useMemo(() => unwrapItems(kbsRes), [kbsRes]);

  const createChangeReqMutation = useCreateChangeRequestMutation();
  const upsertVacationMutation = useUpsertVacationMutation();
  const deleteVacationMutation = useDeleteVacationMutation();
  const createRoleDelegationMutation = useCreateRoleDelegationMutation();
  const instantiateTemplateMutation = useInstantiateTemplateMutation();

  const taskDb = useMemo(
    () => ({ tasks, projects, employees, departments, vacations }),
    [tasks, projects, employees, departments, vacations],
  );
  const projectDb = useMemo(
    () => ({ tasks, projects, employees, kbs, departments }),
    [tasks, projects, employees, kbs, departments],
  );
  const changeReqDb = useMemo(() => ({ tasks, projects }), [tasks, projects]);
  const vacationDb = useMemo(
    () => ({ employees, departments, kbs, vacations }),
    [employees, departments, kbs, vacations],
  );
  const delegationDb = useMemo(() => ({ employees }), [employees]);
  const vacNowDb = useMemo(
    () => ({ employees, departments, kbs, vacations, roleDelegations }),
    [employees, departments, kbs, vacations, roleDelegations],
  );
  const employeeTasksDb = useMemo(
    () => ({ employees, tasks, projects }),
    [employees, tasks, projects],
  );

  const handleError = useCallback(
    (error) => {
      console.error(error);
      if (toast) toast(error.message || "Произошла ошибка", "error");
    },
    [toast],
  );

  const returnToOrigin = useCallback(
    (m, { defaultTaskTab = "form" } = {}) => {
      if (m.returnToTaskId) {
        openTask(
          m.returnToTaskId,
          m.returnToTaskTab || defaultTaskTab,
          null,
          null,
          null,
          "info",
          null,
          "subtasks",
          null,
          null,
          m.returnToTag || null,
        );
        return true;
      }
      if (m.returnToProjectId) {
        openProject(
          m.returnToProjectId,
          m.returnToProjectTab || "info",
          null,
          "info",
          null,
          "form",
          m.returnToTag || null,
        );
        return true;
      }
      return false;
    },
    [openTask, openProject],
  );

  const closeTaskWithReturn = useCallback(() => {
    if (modal.returnToEmployeeTasksId) {
      const range = modal.returnToEmployeeTasksRange || null;
      openEmployeeTasks(
        modal.returnToEmployeeTasksId,
        range?.from || null,
        range?.to || null,
      );
      return;
    }
    if (returnToOrigin(modal, { defaultTaskTab: "subtasks" })) return;
    if (modal.returnToTag) {
      openTag(modal.returnToTag);
      return;
    }
    onClose();
  }, [modal, onClose, openEmployeeTasks, openTag, returnToOrigin]);

  const closeProjectWithReturn = useCallback(() => {
    if (returnToOrigin(modal, { defaultTaskTab: "form" })) return;
    if (modal.returnToTag) {
      openTag(modal.returnToTag);
      return;
    }
    onClose();
  }, [modal, onClose, openTag, returnToOrigin]);

  const copySourceType = modal?.type;
  const modalReturnToTag = modal?.returnToTag || null;

  const handleCopyFromModal = useCallback(
    (sourceId) => {
      if (copySourceType === "task") {
        openCopyTask(sourceId, {
          returnToModal: true,
          returnToTag: modalReturnToTag,
        });
      } else if (copySourceType === "project") {
        openCopyProject(sourceId, {
          returnToModal: true,
          returnToTag: modalReturnToTag,
        });
      }
    },
    [copySourceType, openCopyTask, openCopyProject, modalReturnToTag],
  );

  const handleTaskSave = useCallback(
    (result, isNew, meta) => {
      const templateSubtasks = meta?.templateSubtasks;
      closeTaskWithReturn();

      if (
        !isNew ||
        !result?.id ||
        !result?.projectId ||
        !Array.isArray(templateSubtasks) ||
        templateSubtasks.length === 0
      ) {
        return;
      }

      instantiateTemplateMutation
        .mutateAsync({
          projectId: result.projectId,
          parentTaskId: result.id,
          payloads: templateSubtasks,
        })
        .then((res) => {
          if (!toast || !res) return;

          const created = res.created ?? 0;
          const failed = res.failed ?? 0;
          const drafts = Array.isArray(res.warnings) ? res.warnings.length : 0;
          const active = Math.max(0, created - drafts);

          if (failed > 0) {
            const parts = [];
            if (active > 0) parts.push(`активно ${active}`);
            if (drafts > 0) parts.push(`в черновиках ${drafts}`);
            parts.push(`с ошибками ${failed}`);
            toast(`Задача создана. Подзадачи: ${parts.join(", ")}`, "warning");
            return;
          }

          if (drafts > 0) {
            toast(
              `Задача создана. Подзадачи: активно ${active}, в черновиках ${drafts}`,
              "warning",
            );
            return;
          }

          if (created > 0) {
            toast(`Задача и ${created} подзадач(и) созданы`, "success");
          }
        })
        .catch((err) => {
          if (toast)
            toast(
              err?.message || "Задача создана, подзадачи — с ошибкой",
              "warning",
            );
        });
    },
    [closeTaskWithReturn, instantiateTemplateMutation, toast],
  );

  const handleTaskDelete = useCallback(async () => {
    closeTaskWithReturn();
  }, [closeTaskWithReturn]);

  const handleProjectSave = useCallback(async () => {
    closeProjectWithReturn();
  }, [closeProjectWithReturn]);

  const handleProjectDelete = useCallback(async () => {
    closeProjectWithReturn();
  }, [closeProjectWithReturn]);

  const handleChangeReqSubmit = useCallback(
    async (r) => {
      try {
        const recipientIds = employees
          .filter(
            (e) =>
              !e.fired &&
              (e.roles.includes("director") || e.roles.includes("admin")),
          )
          .map((e) => e.id);

        await createChangeReqMutation.mutateAsync({ ...r, recipientIds });
        onClose();
      } catch (error) {
        handleError(error);
      }
    },
    [employees, createChangeReqMutation, onClose, handleError],
  );

  const handleVacationSave = useCallback(
    async (v, isNew) => {
      try {
        await upsertVacationMutation.mutateAsync({
          id: isNew ? undefined : v.id,
          body: v,
        });
        onClose();
      } catch (error) {
        handleError(error);
      }
    },
    [upsertVacationMutation, onClose, handleError],
  );

  const handleVacationDelete = useCallback(
    async (id) => {
      await deleteVacationMutation.mutateAsync(id);
    },
    [deleteVacationMutation],
  );

  const handleDelegationSubmit = useCallback(
    async (rd) => {
      try {
        await createRoleDelegationMutation.mutateAsync(rd);
        onClose();
      } catch (error) {
        handleError(error);
      }
    },
    [createRoleDelegationMutation, onClose, handleError],
  );

  if (!modal) return null;

  switch (modal.type) {
    case "task":
      return (
        <TaskModal
          db={taskDb}
          ur={ur}
          taskId={modal.taskId}
          copyFromId={modal.copyFromId}
          initialTab={modal.initialTab || "form"}
          parentTaskId={modal.parentTaskId}
          initialProjectId={modal.initialProjectId}
          returnToProjectId={modal.returnToProjectId}
          returnToTaskId={modal.returnToTaskId}
          returnToEmployeeTasksId={modal.returnToEmployeeTasksId}
          returnToTag={modal.returnToTag || null}
          highlightFileId={modal.highlightFileId || null}
          highlightFolderId={modal.highlightFolderId || null}
          onClose={closeTaskWithReturn}
          onCopy={handleCopyFromModal}
          onTabChange={onTabChange}
          onSave={handleTaskSave}
          onDelete={handleTaskDelete}
          onChangeReq={openChangeReq}
          openTask={openTask}
          openProject={openProject}
          openTag={openTag}
          toast={toast}
        />
      );

    case "project":
      return (
        <ProjectModal
          db={projectDb}
          ur={ur}
          projectId={modal.projectId}
          copyFromId={modal.copyFromId}
          returnToProjectId={modal.returnToProjectId}
          returnToTaskId={modal.returnToTaskId}
          returnToTaskTab={modal.returnToTaskTab}
          returnToTag={modal.returnToTag || null}
          initialTab={modal.initialTab || "info"}
          onClose={closeProjectWithReturn}
          onCopy={handleCopyFromModal}
          onTabChange={onTabChange}
          onSave={handleProjectSave}
          onDelete={handleProjectDelete}
          onChangeReq={openChangeReq}
          openTask={openTask}
          openTag={openTag}
          toast={toast}
        />
      );

    case "changeReq":
      return (
        <ChangeRequestModal
          db={changeReqDb}
          ur={ur}
          changeKind={modal.changeKind}
          targetType={modal.targetType}
          targetId={modal.targetId}
          onClose={onClose}
          onSubmit={handleChangeReqSubmit}
          toast={toast}
        />
      );

    case "roles":
      return (
        <RolesModal empId={modal.employeeId} onClose={onClose} toast={toast} />
      );

    case "depts":
      return (
        <DeptsModal empId={modal.employeeId} onClose={onClose} toast={toast} />
      );

    case "vacation":
      return (
        <VacationModal
          db={vacationDb}
          ur={ur}
          vacationId={modal.vacationId}
          forEmpId={modal.forEmpId || null}
          onClose={onClose}
          onSave={handleVacationSave}
          onDelete={handleVacationDelete}
          toast={toast}
        />
      );

    case "delegation":
      return (
        <DelegationModal
          db={delegationDb}
          ur={ur}
          onClose={onClose}
          onSubmit={handleDelegationSubmit}
          toast={toast}
        />
      );

    case "vacnow":
      return <VacNowModal db={vacNowDb} onClose={onClose} toast={toast} />;

    case "yearCalendar":
      return <YearCalendarModal onClose={onClose} />;

    case "employeeTasks":
      return (
        <EmployeeTasksModal
          db={employeeTasksDb}
          employeeId={modal.employeeId}
          fromIso={modal.fromIso || null}
          toIso={modal.toIso || null}
          openTask={openTask}
          onClose={onClose}
        />
      );

    case "templateFromTask": {
      const task = tasks.find((t) => t.id === modal.taskId);
      if (!task) return null;
      return (
        <TemplateModal
          mode="create"
          kind="task"
          source={task}
          nested={collectTaskPayloads(tasks, task.id)}
          onClose={onClose}
          toast={toast}
        />
      );
    }

    case "templateFromProject": {
      const project = projects.find((p) => p.id === modal.projectId);
      if (!project) return null;
      return (
        <TemplateModal
          mode="create"
          kind="project"
          source={project}
          nested={collectTaskPayloads(
            tasks.filter((t) => t.projectId === project.id && !t.archived),
          )}
          onClose={onClose}
          toast={toast}
        />
      );
    }

    case "createEmployee":
      return <CreateEmployeeModal onClose={onClose} toast={toast} />;

    case "editEmployee":
      return (
        <EditEmployeeModal
          ur={ur}
          employeeId={modal.employeeId}
          onClose={onClose}
          toast={toast}
        />
      );

    case "tag": {
      const { tag, delegationId } = modal;
      const handleOpenTaskFromTag = (id) =>
        openTask(
          id,
          "form",
          null,
          null,
          null,
          "info",
          null,
          "subtasks",
          null,
          null,
          tag,
        );
      const handleOpenProjectFromTag = (id) =>
        openProject(id, "info", null, "info", null, "form", tag);
      return (
        <TagResultsModal
          tag={tag}
          delegationId={delegationId}
          openTask={handleOpenTaskFromTag}
          openProject={handleOpenProjectFromTag}
          onClose={onClose}
        />
      );
    }

    case "help":
      return <HelpModal onClose={onClose} />;

    default:
      return null;
  }
}
